import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Formik, Form } from 'formik';

import {
  SELF_SHOWING_ISSUE_TYPE_DOOR_LOCKED,
  SELF_SHOWING_ISSUE_TYPE_OTHER,
} from 'common/selfShowingLockConstants';

import { initialApiActionState } from 'api/createApiClient';
import { SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR } from 'api/actionTypes';

import {
  hideFieldIf,
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_PARTNER_FORM } from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';
import useDispatch from 'client/hooks/useDispatch';
import useBranch from 'client/hooks/useBranch';

function getFormFields({ doorHandleLocked }) {
  return [
    {
      id: 'doorHandleLocked',
      title: '',
      type: 'yes_no',
      validations: ['required'],
      classnames: 'selfTourLockForm partnerForm adobe',
    },
    hideFieldIf(doorHandleLocked !== false, {
      id: 'doorHandleLockedFeedback',
      title: '',
      properties: {
        placeholder: 'Share your feedback',
      },
      type: 'long_text',
      validations: ['required'],
      classnames: 'selfTourLockForm partnerForm adobe',
    }),
  ];
}

const SrSelfShowingLockForm = ({
  userId,
  listing,
  onClose,
  clientName,
  clientPhone,
  clientEmail,
  accessCodeShown,
  selfTouringState,
}) => {
  const dispatch = useDispatch();
  const { apiStateReport = initialApiActionState } = useBranch({
    apiStateReport: [
      'apiState',
      SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR,
      'selfShowingLockReportIssueApiState',
    ],
  });

  const onSubmit = (formValues) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR,
      queryId: 'selfShowingLockReportIssueApiState',
      payload: {
        userId: userId,
        listingId: listing.id,
        clientName: clientName,
        clientPhone: clientPhone,
        clientEmail: clientEmail,
        accessCodeShown: accessCodeShown,
        doorHandleLocked: formValues.doorHandleLocked,
        issue: formValues.doorHandleLockedFeedback || '--',
        issueType: formValues.doorHandleLocked
          ? SELF_SHOWING_ISSUE_TYPE_DOOR_LOCKED
          : SELF_SHOWING_ISSUE_TYPE_OTHER,
      },
      onSuccess: () => {
        onClose({ showContactSupportModal: true });
      },
    });
  };

  const listingPhoto = listing.sunroomPhotoUrls[0] || '/images/self-showing-lock-form.svg';

  return (
    <SrDarkModal>
      <div
        className={classnames(
          'SrListingSelfTouringSection sunlockStatusModal sunlockLockForm',
          selfTouringState,
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <SrFlex width="100%" justify="center" marginBottom="18px">
          <img
            src={listingPhoto}
            alt="Listing"
            width="207px"
            height="120"
            style={{ borderRadius: 8 }}
          />
        </SrFlex>
        <SrFlex marginBottom="16px" justify="center">
          <SrText
            color="blueGreycliff"
            font="greycliff"
            weight="800"
            fontSize="21px"
            lineHeight="30px"
            align="center"
          >
            Was the door handle locked?
          </SrText>
        </SrFlex>
        <SrFlex marginBottom="16px" justify="center">
          <SrText
            color="blueGreycliff"
            font="greycliff"
            weight="600"
            fontSize="16px"
            lineHeight="25px"
            align="center"
          >
            Try twisting the door handle. Does the handle not twist, indicating that the handle
            itself is locked?
          </SrText>
        </SrFlex>
        <SrFlex height="350px" direction="col">
          <Formik
            validate={(formValues) => {
              const formFields = getFormFields({ doorHandleLocked: formValues.doorHandleLocked });
              const errors = createFormValidationFn(formFields)(formValues);

              return errors;
            }}
            enableReinitialize
            initialValues={createInitialValues({
              formFields: getFormFields({
                doorHandleLocked: null,
              }),
            })}
            onSubmit={onSubmit}
            render={(formikBag) => {
              const formFields = getFormFields({
                doorHandleLocked: formikBag.values.doorHandleLocked,
              });
              const buttonType = formikBag.isValid ? 'submit' : 'button';
              const buttonTheme = formikBag.isValid
                ? SR_BUTTON_THEME_ADOBE_GREEN
                : SR_BUTTON_THEME_ADOBE_GREY;

              return (
                <Form autoComplete="off" style={{ height: '100%' }}>
                  <SrFlex direction="col" justify="between" height="100%">
                    <SrBox>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          className={formField.classnames || SR_FIELD_THEME_PARTNER_FORM}
                        />
                      ))}
                    </SrBox>
                    <SrBox>
                      <SrButton
                        type={buttonType}
                        theme={buttonTheme}
                        disabled={apiStateReport.inProgress}
                      >
                        Submit
                      </SrButton>
                    </SrBox>
                  </SrFlex>
                </Form>
              );
            }}
          />
        </SrFlex>
      </div>
    </SrDarkModal>
  );
};

SrSelfShowingLockForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  selfTouringState: PropTypes.string.isRequired,
  listing: PropTypes.object.isRequired,
  userId: PropTypes.number.isRequired,
  accessCodeShown: PropTypes.bool.isRequired,
};

SrSelfShowingLockForm.displayName = 'SrSelfShowingLockForm';

export default SrSelfShowingLockForm;
